import { useState, useCallback } from "react";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSEnumSelect from "views/system-design/DSEnumSelect";
import DSTextField from "views/system-design/DSTextField";
import DSChip from "views/system-design/DSChip";
import DSIconButton from "views/system-design/DSIconButton";
import DSAutocomplete from "views/system-design/DSAutocomplete";
import AddIcon from "@mui/icons-material/Add";
import DSSwitch from "views/system-design/DSSwitch";
import DSFormControlLabel from "views/system-design/DSFormControlLabel";
import {
  Ticket,
  TicketStatus,
  TicketType,
  TICKET_STATUSES,
  TICKET_TYPES,
  TICKET_PRIORITIES,
  REPO_LABELS,
} from "../../types";
import { useTickets } from "../../context/TicketsContext";
import {
  getAssignableUsers,
  canChangeStatus,
  sanitizeVersion,
  findUserByAssignee,
} from "../../utils";
import useUsers from "components/hooks/useUsers";
import { Author } from "api/author";
import useAutoSave from "../../hooks/useAutoSave";
import ShopifyPageChips from "../shared/ShopifyPageChips";

interface TicketSidePanelProps {
  ticket: Ticket;
}

function SidePanelField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <DSBox sx={{ mb: 2.5 }}>
      <DSTypography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 600,
          mb: 0.5,
          display: "block",
        }}
      >
        {label}
      </DSTypography>
      {children}
    </DSBox>
  );
}

export default function TicketSidePanel({ ticket }: TicketSidePanelProps) {
  const { updateTicket } = useTickets();
  const { data: allUsers } = useUsers();
  const assigneeOptions = getAssignableUsers(allUsers ?? []);
  const [newLabel, setNewLabel] = useState("");

  const update = useCallback(
    (updates: Partial<Ticket>) => {
      updateTicket(ticket.id, updates);
    },
    [updateTicket, ticket.id],
  );

  const versionSave = useCallback(
    (value: string) => update({ targetVersion: value }),
    [update],
  );
  const versionField = useAutoSave(
    ticket.id,
    ticket.targetVersion,
    versionSave,
    sanitizeVersion,
  );

  const urlSave = useCallback(
    (value: string) => update({ url: value }),
    [update],
  );
  const urlField = useAutoSave(ticket.id, ticket.url ?? "", urlSave);

  const addLabel = () => {
    const trimmed = newLabel.trim();
    if (trimmed && !ticket.labels.includes(trimmed)) {
      update({ labels: [...ticket.labels, trimmed] });
    }
    setNewLabel("");
  };

  const removeLabel = (label: string) => {
    update({
      labels: ticket.labels.filter((existing) => existing !== label),
    });
  };

  return (
    <DSBox>
      <SidePanelField label="Status">
        <DSEnumSelect<TicketStatus>
          value={ticket.status}
          options={TICKET_STATUSES}
          onChange={(status) => {
            if (!canChangeStatus(ticket, status)) return;
            update({ status });
          }}
          size="small"
          fullWidth
        />
      </SidePanelField>

      <SidePanelField label="Type">
        <DSEnumSelect<TicketType>
          value={ticket.type}
          options={TICKET_TYPES}
          onChange={(type) => update({ type })}
          size="small"
          fullWidth
        />
      </SidePanelField>

      <SidePanelField label="Priority">
        <DSEnumSelect
          value={ticket.priority ?? "none"}
          options={[{ value: "none", label: "None" }, ...TICKET_PRIORITIES]}
          onChange={(priority) =>
            update({ priority: priority === "none" ? undefined : priority })
          }
          size="small"
          fullWidth
        />
      </SidePanelField>

      <SidePanelField label="Assignee">
        <DSAutocomplete<Author>
          options={assigneeOptions}
          getOptionLabel={(option) => option.name.trim()}
          value={findUserByAssignee(ticket.assignee, assigneeOptions)}
          onChange={(_event, selectedUser) =>
            update({ assignee: selectedUser?.id ?? "" })
          }
          size="small"
          renderInput={(params) => (
            <DSTextField {...params} placeholder="Unassigned" size="small" />
          )}
        />
      </SidePanelField>

      <SidePanelField label="Creator">
        <DSAutocomplete<Author>
          options={assigneeOptions}
          getOptionLabel={(option) => option.name.trim()}
          value={
            assigneeOptions.find((user) => user.id === ticket.creatorId) ?? null
          }
          onChange={(_event, selectedUser) =>
            update({ creatorId: selectedUser?.id ?? "" })
          }
          size="small"
          renderInput={(params) => (
            <DSTextField {...params} placeholder="No creator" size="small" />
          )}
        />
      </SidePanelField>

      <SidePanelField label="Due Date">
        <DSTextField
          type="date"
          value={ticket.dueDate ? ticket.dueDate.split("T")[0] : ""}
          onChange={(event) =>
            update({
              dueDate: event.target.value
                ? new Date(event.target.value).toISOString()
                : null,
            })
          }
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      </SidePanelField>

      <SidePanelField label="Target Version">
        <DSTextField
          value={versionField.draft}
          onChange={(event) => versionField.setDraft(event.target.value)}
          onBlur={versionField.flush}
          size="small"
          fullWidth
          placeholder="e.g. 7.2.0"
        />
      </SidePanelField>

      <SidePanelField label="URL">
        <DSTextField
          value={urlField.draft}
          onChange={(event) => urlField.setDraft(event.target.value)}
          onBlur={urlField.flush}
          size="small"
          fullWidth
          placeholder="https://..."
        />
      </SidePanelField>

      {ticket.labels.includes("shopify") && (
        <SidePanelField label="Shopify Page">
          <ShopifyPageChips
            selectedPage={ticket.shopifyPage ?? ""}
            onPageChange={(page) => update({ shopifyPage: page })}
          />
        </SidePanelField>
      )}

      <SidePanelField label="Backlog">
        <DSFormControlLabel
          control={
            <DSSwitch
              checked={ticket.isBacklog}
              onChange={(event) => update({ isBacklog: event.target.checked })}
              size="small"
            />
          }
          label={ticket.isBacklog ? "Yes" : "No"}
        />
      </SidePanelField>

      <SidePanelField label="Archive">
        <DSFormControlLabel
          control={
            <DSSwitch
              checked={ticket.isArchived}
              onChange={(event) => update({ isArchived: event.target.checked })}
              size="small"
            />
          }
          label={ticket.isArchived ? "Yes" : "No"}
        />
      </SidePanelField>

      <SidePanelField label="Labels">
        <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
          {ticket.labels.map((label) => (
            <DSChip
              key={label}
              label={label}
              size="small"
              onDelete={() => removeLabel(label)}
            />
          ))}
        </DSBox>
        <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
          {REPO_LABELS.filter(
            (repoLabel) => !ticket.labels.includes(repoLabel),
          ).map((repoLabel) => (
            <DSChip
              key={repoLabel}
              label={repoLabel}
              size="small"
              variant="outlined"
              onClick={() => update({ labels: [...ticket.labels, repoLabel] })}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </DSBox>
        <DSBox sx={{ display: "flex", gap: 0.5 }}>
          <DSTextField
            value={newLabel}
            onChange={(event) => setNewLabel(event.target.value)}
            size="small"
            placeholder="Custom label"
            fullWidth
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addLabel();
              }
            }}
          />
          <DSIconButton size="small" onClick={addLabel}>
            <AddIcon fontSize="small" />
          </DSIconButton>
        </DSBox>
      </SidePanelField>
    </DSBox>
  );
}
